import React, { useState, useCallback, useEffect } from 'react'
import classnames from 'classnames'

import {
  PreMakingTemplateDetail,
  PreviousDayOrderCountsForTemplateResponse,
} from '../../../core/pre-making'
import PreMakingTemplateScheduleManager from './pre-making-template-schedule-manager'
import PreMakingTemplateInfoForm from './pre-making-template-info-form'
import PreviousDayOrderCountsSection from './previous-day-order-counts-section'
import { preMakingContent } from './pre-making-content'
import tableStyles from '../../styles/modules/table.module.scss'
import styles from '../../styles/modules/pre-making-detail/pre-making-detail-table.module.scss'
import preMakingDetailStyles from '../../styles/modules/pre-making-detail/pre-making-schedule-manager.module.scss'
import Button from '../button'

interface Props {
  templateDetail: PreMakingTemplateDetail
  isLoading: boolean
  previousDayOrderCounts: PreviousDayOrderCountsForTemplateResponse | null
  isLoadingPreviousDayOrderCounts: boolean
  onApplyPreviousDayOrderCounts: () => void
  onSave: (
    name: string,
    description: string,
    items: EditableItem[],
    dates?: Date[]
  ) => void
  onDelete?: () => void
}

interface EditableItem {
  timeSlotID: number
  itemID: number
  quantity: number
}

const PreMakingTemplateDetailTable: React.FC<Props> = ({
  templateDetail,
  isLoading,
  previousDayOrderCounts,
  isLoadingPreviousDayOrderCounts,
  onApplyPreviousDayOrderCounts,
  onSave,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState<{ [key: string]: number }>({})
  const [editDates, setEditDates] = useState<Date[]>([])
  const [editTemplateName, setEditTemplateName] = useState('')
  const [editTemplateDescription, setEditTemplateDescription] = useState('')
  const [applyMessage, setApplyMessage] = useState<string | null>(null)
  const [shouldApplyPreviousDayCounts, setShouldApplyPreviousDayCounts] =
    useState(false)

  const getQuantityForDisplay = useCallback(
    (timeSlotID: number, itemID: number) => {
      if (isEditing) {
        return editData[`${timeSlotID}_${itemID}`] || 0
      }

      return (
        templateDetail.matrix[timeSlotID.toString()]?.[itemID.toString()]
          ?.quantity || 0
      )
    },
    [editData, isEditing, templateDetail.matrix]
  )

  const handleDatesChange = useCallback((dates: Date[]) => {
    setEditDates(dates)
  }, [])

  const buildEditDataFromMatrix = useCallback(
    (
      matrix: {
        [timeSlotId: string]: { [itemId: string]: { orderCount: number } }
      }
    ) => {
      const result: { [key: string]: number } = {}

      templateDetail.timeSlots.forEach(timeSlot => {
        templateDetail.uniqueItems.forEach(item => {
          const quantity =
            matrix[timeSlot.id.toString()]?.[item.id.toString()]?.orderCount || 0
          result[`${timeSlot.id}_${item.id}`] = quantity
        })
      })

      return result
    },
    [templateDetail]
  )

  const handleStartEdit = useCallback(() => {
    const initialData: { [key: string]: number } = {}

    Object.keys(templateDetail.matrix).forEach(timeSlotId => {
      Object.keys(templateDetail.matrix[timeSlotId]).forEach(itemId => {
        const key = `${timeSlotId}_${itemId}`
        initialData[key] = templateDetail.matrix[timeSlotId][itemId].quantity
      })
    })

    setEditData(initialData)

    setEditTemplateName(templateDetail.template.name)
    setEditTemplateDescription(templateDetail.template.description)

    if (
      templateDetail.template.templateType === 'custom' &&
      templateDetail.schedules
    ) {
      const dates = templateDetail.schedules
        .filter(s => s.selectedDate)
        .map(s => new Date(s.selectedDate as string))
      setEditDates(dates)
    }

    setIsEditing(true)
  }, [templateDetail])

  const handleApplyClick = useCallback(() => {
    if (!isEditing) {
      setApplyMessage('「編集」を押してから前日杯数を適用してください')
      return
    }

    setApplyMessage(null)
    setShouldApplyPreviousDayCounts(true)
    onApplyPreviousDayOrderCounts()
  }, [isEditing, onApplyPreviousDayOrderCounts])

  useEffect(() => {
    if (!previousDayOrderCounts || !shouldApplyPreviousDayCounts) {
      return
    }

    if (!isEditing) {
      handleStartEdit()
    }

    if (previousDayOrderCounts.hasData) {
      const updatedData = buildEditDataFromMatrix(previousDayOrderCounts.matrix)
      setEditData(updatedData)
      setApplyMessage(
        `前日(${previousDayOrderCounts.previousDate})の注文数を適用しました`
      )
    } else {
      setApplyMessage(previousDayOrderCounts.message || null)
    }

    setShouldApplyPreviousDayCounts(false)
  }, [
    previousDayOrderCounts,
    shouldApplyPreviousDayCounts,
    isEditing,
    handleStartEdit,
    buildEditDataFromMatrix,
  ])

  useEffect(() => {
    if (
      !isLoadingPreviousDayOrderCounts &&
      shouldApplyPreviousDayCounts &&
      !previousDayOrderCounts
    ) {
      setShouldApplyPreviousDayCounts(false)
    }
  }, [
    isLoadingPreviousDayOrderCounts,
    shouldApplyPreviousDayCounts,
    previousDayOrderCounts,
  ])

  if (isLoading) {
    return (
      <div>
        <div>{preMakingContent.loadingMessage}</div>
      </div>
    )
  }

  // 編集キャンセル
  const handleCancel = () => {
    setIsEditing(false)
    setEditData({})
    setEditDates([])
    setApplyMessage(null)
    setShouldApplyPreviousDayCounts(false)
  }

  // 保存
  const handleSave = () => {
    // カスタムテンプレートの場合は名前と説明の空白のみチェック
    if (templateDetail.template.templateType === 'custom') {
      if (editTemplateName.trim() === '') {
        alert('テンプレート名を入力してください')
        return
      }
      if (editTemplateDescription.trim() === '') {
        alert('テンプレート説明を入力してください')
        return
      }
    }

    const items: EditableItem[] = []
    Object.keys(editData).forEach(key => {
      const quantity = editData[key]
      if (quantity > 0) {
        const [timeSlotID, itemID] = key.split('_').map(Number)
        items.push({ timeSlotID, itemID, quantity })
      }
    })

    // カスタムテンプレートの場合は日付も送信（date-time形式）
    let dates: Date[] | undefined = undefined
    if (templateDetail.template.templateType === 'custom') {
      dates = editDates.sort((a, b) => a.getTime() - b.getTime())
    }

    onSave(
      editTemplateName.trim(),
      editTemplateDescription.trim(),
      items,
      dates
    )
    setIsEditing(false)
    setEditData({})
    setEditDates([])
    setApplyMessage(null)
    setShouldApplyPreviousDayCounts(false)
  }

  // 数量変更
  const handleQuantityChange = (
    timeSlotID: number,
    itemID: number,
    value: string
  ) => {
    const key = `${timeSlotID}_${itemID}`
    const numValue = value === '' ? 0 : parseInt(value, 10)
    if (!isNaN(numValue) && numValue >= 0) {
      setEditData(prev => ({
        ...prev,
        [key]: numValue,
      }))
    }
  }

  // 行の合計を計算
  const calculateRowTotal = (timeSlot: { id: number }) => {
    return templateDetail.uniqueItems.reduce((total, item) => {
      return total + getQuantityForDisplay(timeSlot.id, item.id)
    }, 0)
  }

  // 保存ボタンの無効状態判定
  const isSaveDisabled = () => {
    if (templateDetail.template.templateType === 'custom') {
      return (
        editTemplateName.trim() === '' || editTemplateDescription.trim() === ''
      )
    }
    return false
  }

  return (
    <>
      {/* 編集ボタン・削除ボタン・保存ボタン */}
      <div style={{ marginBottom: '16px' }}>
        {isEditing ? (
          <>
            <Button
              className={styles.button}
              title="やめる"
              onClick={handleCancel}
            />
            <Button
              className={styles.button}
              title="保存"
              onClick={handleSave}
              disabled={isSaveDisabled()}
            />
          </>
        ) : (
          <>
            <Button
              className={styles.button}
              title="編集"
              onClick={handleStartEdit}
            />
            {onDelete && (
              <Button
                className={styles.deleteButton}
                title="削除"
                onClick={onDelete}
              />
            )}
          </>
        )}
      </div>

      {applyMessage && (
        <div className={styles.applyMessage}>{applyMessage}</div>
      )}

      <h3 className={tableStyles.title}>テンプレート情報</h3>
      {/* テンプレート情報 */}
      <PreMakingTemplateInfoForm
        template={templateDetail.template}
        isEditing={isEditing}
        editTemplateName={editTemplateName}
        editTemplateDescription={editTemplateDescription}
        onNameChange={setEditTemplateName}
        onDescriptionChange={setEditTemplateDescription}
      />

      {/* カスタムテンプレートの場合、適用日管理を表示 */}
      <PreMakingTemplateScheduleManager
        templateType={templateDetail.template.templateType}
        selectedDates={
          templateDetail.schedules
            ? templateDetail.schedules
                .filter(s => s.selectedDate)
                .map(s => s.selectedDate as string)
            : []
        }
        isEditing={isEditing}
        editDates={editDates}
        onDatesChange={handleDatesChange}
      />

      {/* 調理杯数セクションタイトル */}
      {templateDetail.uniqueItems.length > 0 && (
        <div className={styles.sectionHeader}>
          <h3 className={tableStyles.title}>調理杯数</h3>
          <Button
            className={styles.button}
            title={preMakingContent.applyPreviousDayOrdersButtonTitle}
            onClick={handleApplyClick}
            disabled={!isEditing || isLoadingPreviousDayOrderCounts}
          />
        </div>
      )}
      <div className={preMakingDetailStyles.warnText}>
        ※ 調理杯数を編集する際は、該当の時間枠が始まる前に保存してください。
      </div>

      {templateDetail.uniqueItems.length > 0 ? (
        <div
          className={classnames(
            tableStyles.wrapper,
            tableStyles.zebra,
            styles.preMakingTableWrapper
          )}
          style={{ marginTop: '2.4rem' }}
        >
          <table className={tableStyles.container}>
            <thead>
              <tr className={tableStyles.row}>
                <th className={tableStyles.cell} style={{ width: '80px' }}>
                  時間枠
                </th>
                <th className={tableStyles.cell} style={{ width: '24px' }}>
                  合計
                </th>
                {templateDetail.uniqueItems.map(item => (
                  <th key={`header-${item.id}`} className={tableStyles.cell}>
                    {item.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {templateDetail.timeSlots
                .filter(timeSlot => {
                  const hour = parseInt(timeSlot.from.split(':')[0], 10)
                  return hour >= 6 && hour < 23
                })
                .sort((a, b) => a.id - b.id)
                .map(timeSlot => (
                  <tr key={timeSlot.id} className={tableStyles.row}>
                    <td className={tableStyles.cell}>
                      {timeSlot.from}~{timeSlot.to}
                    </td>
                    <td className={tableStyles.cell}>
                      {calculateRowTotal(timeSlot)}
                    </td>
                    {templateDetail.uniqueItems.map(item => {
                      const currentQuantity = getQuantityForDisplay(
                        timeSlot.id,
                        item.id
                      )

                      return (
                        <td
                          key={`cell-${timeSlot.id}-${item.id}`}
                          className={tableStyles.cell}
                        >
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              value={currentQuantity}
                              onChange={e =>
                                handleQuantityChange(
                                  timeSlot.id,
                                  item.id,
                                  e.target.value
                                )
                              }
                              // ホイールスクロール時に勝手に数値が変わってしまう問題を解決するためのコード
                              onFocus={e =>
                                e.target.addEventListener(
                                  'wheel',
                                  e => {
                                    e.preventDefault()
                                  },
                                  { passive: false }
                                )
                              }
                              className={styles.numberInput}
                            />
                          ) : (
                            currentQuantity
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div>
          {isEditing && <p>編集モードでは全てのアイテムが表示されます。</p>}
          <div>このテンプレートにはアイテムが登録されていません。</div>
        </div>
      )}

      {/* 前日提供杯数セクション */}
      <PreviousDayOrderCountsSection
        previousDayOrderMatrix={
          previousDayOrderCounts?.matrix ||
          templateDetail.previousDayOrderMatrix ||
          undefined
        }
        timeSlots={templateDetail.timeSlots}
        uniqueItems={templateDetail.uniqueItems}
      />
    </>
  )
}

export default PreMakingTemplateDetailTable
